import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-listen-8 text-sm ring-offset-background transition-[background-color,border-color,color,box-shadow] focus-visible:outline-none focus-visible:brand-focus disabled:pointer-events-none disabled:opacity-60 border border-transparent",
  {
    variants: {
      variant: {
        cta: "bg-surface-brand-primary text-content-brand-contrast hover:bg-surface-brand-primary/90 active:bg-surface-brand-primary/95",
        // Interview-branded variants: only uses classes that are part of the theme.
        // Needed to ensure that text color matches background of theme.
        customBrandPrimary:
          "bg-surface-inverse-primary text-content-inverse-primary hover:bg-surface-inverse-primary/90 active:bg-surface-inverse-primary/95",
        customBrandSecondary:
          "border-content-brand/30 text-content-brand bg-transparent hover:bg-content-brand/5",
        // Interview-branded destructive: red is the global negative token (not
        // study-themed); text follows the study background like customBrandPrimary.
        customDestructive:
          "bg-content-negative text-content-inverse-primary hover:bg-content-negative/90 active:bg-content-negative/95 disabled:bg-content-negative/60 disabled:text-content-inverse-primary disabled:opacity-100",
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        destructiveLight: "bg-red-50 text-red-500",
        outline:
          "border border-surface-tertiary bg-surface-highlight hover:bg-surface-primary active:bg-surface-secondary",
        surface:
          "bg-surface-highlight text-content-primary border border-transparent hover:border-content-primary",
        brandSecondary:
          "bg-[hsl(var(--surface-brand-secondary)/0.1)] text-content-brand hover:bg-[hsl(var(--surface-brand-secondary)/0.16)] active:bg-[hsl(var(--surface-brand-secondary)/0.2)]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/90 border border-transparent",
        ghost:
          "hover:bg-surface-secondary text-content-secondary hover:text-content-primary",
        link: "text-primary underline-offset-4 hover:underline",
        subtle: "bg-black/[0.05] hover:bg-black/[0.10] text-neutral-600",
        tag: "bg-surface-secondary text-content-primary hover:bg-surface-tertiary",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-listen-8 px-2 py-2",
        xxs: "h-6 rounded-listen-8 px-1 py-0.5 text-xs",
        xs: "h-7 rounded-listen-8 px-2 text-xs",
        lg: "h-11 rounded-lg px-8 text-base",
        icon: "h-10 w-10 rounded-lg",
        smIcon: "h-8 w-8 rounded-listen-8", // same as xs input
        xsIcon: "h-7 w-7 rounded-listen-8",
        xxsIcon: "h-6 w-6 rounded-listen-8",
        listenSm: "h-6 rounded-listen-8 px-2 text-listen-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
